import { authQueryKeys, orgQueryKeys } from '@/config/queryKeys';
import { ORGS_QUERY_GC_TIME, ORGS_QUERY_STALE_TIME } from '@/constants';
import { useLogout } from '@/hooks';
import { useApiClient } from '@/hooks/useApiClient';
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { useAppStore } from '@/stores/app.ts';
import { Org, User } from '@/types';
import { FullLoading } from '@components/Loaders';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

export const Route = createFileRoute('/')({
  component: () => {
    // Handle post-login redirect
    usePostLoginRedirect();

    // Detect when we've just come back from a server-side login redirect.
    // On the very first /auth/me call the session cookie may not yet be
    // visible (timing / cookie-propagation race), so we give the query a
    // one-shot retry before treating null as "unauthenticated".
    const isPostLoginCallback = window.location.search.includes('login=success');

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { get } = useApiClient();
    const {
      data: authData,
      error,
      isSuccess,
    } = useSuspenseQuery({
      queryKey: authQueryKeys.me(),
      queryFn: async () => {
        try {
          const user = await get<User | null>('/auth/me');
          return user;
        } catch (err: any) {
          if (err?.status === 401) return null;
          if (err?.status === 403) throw new Error('FORBIDDEN', { cause: err });
          throw new Error(`Auth check failed: ${err?.status || err}`, {
            cause: err,
          });
        }
      },
      staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    });
    const { data, isPending } = useQuery<Org[]>({
      enabled: !!authData?.id && isSuccess,
      queryKey: orgQueryKeys.list(),
      queryFn: fetchOrganizations,
      staleTime: ORGS_QUERY_STALE_TIME,
      gcTime: ORGS_QUERY_GC_TIME,
    });
    const { t } = useTranslation('login');
    const logout = useLogout();

    const { setSelectedOrg, selectedOrganization } = useAppStore(
      useShallow(state => ({
        setSelectedOrg: state.setSelectedOrganization,
        selectedOrganization: state.selectedOrganization,
      })),
    );
    // If 401/403, go login.
    // Skip the redirect on the post-login callback URL (?login=success) so we
    // don't bounce back to /login before the session cookie has propagated.
    useEffect(() => {
      if (error?.message === 'FORBIDDEN') {
        logout.mutate();
        navigate({ to: '/login' });
      } else if (!authData && !isPostLoginCallback) {
        navigate({ to: '/login' });
      }
    }, [error, authData, isPostLoginCallback, logout, navigate]);

    // When landing on /?login=success and auth initially returns null (401
    // before cookie propagation), schedule a one-shot refetch so the query
    // picks up the now-valid session without a full round-trip to /login.
    const didScheduleRefetch = useRef(false);
    useEffect(() => {
      if (isPostLoginCallback && !authData && !didScheduleRefetch.current) {
        didScheduleRefetch.current = true;
        const timer = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: authQueryKeys.me() });
        }, 800);
        return () => clearTimeout(timer);
      }
      return undefined;
    }, [authData, isPostLoginCallback, queryClient]);

    // Use authData (from useSuspenseQuery) rather than user (from useAuthStatus)
    // to select the org. Both represent the same authenticated user, but
    // useAuthStatus runs an independent query that may still be pending when
    // authData has already resolved — causing the effect to silently no-op and
    // leaving selectedOrganization null, which stalls the redirect to /studies.
    useEffect(() => {
      if (data && data.length > 0 && authData) {
        // authData is typed as User (= Schema<'CurrentUser'>), which has id: string — same field as the former user.id from useAuthStatus
        const storedOrgId = localStorage.getItem(`selectedOrgId_${authData.id}`);
        const storedOrg = data.find(org => String(org.id) === storedOrgId);
        const orgToSelect = storedOrg || data[0];

        setSelectedOrg(orgToSelect);
        // Only set localStorage if the stored value is missing or invalid
        if (!storedOrgId || !storedOrg) {
          localStorage.setItem(`selectedOrgId_${authData.id}`, String(orgToSelect.id));
        }
      }
    }, [data, setSelectedOrg, authData]);

    if (selectedOrganization) {
      return <Navigate to="/studies" />;
    }

    if (isPending) {
      return <FullLoading label={t('loading.default')} />;
    }

    // Authenticated user with no orgs — send to the no-org landing page
    if (authData !== null && data?.length === 0) {
      return <Navigate to="/no_org" />;
    }

    // Still waiting for the session cookie to propagate after a fresh login —
    // show a spinner rather than bouncing to /login prematurely.
    if (isPostLoginCallback && !authData) {
      return <FullLoading label={t('loading.default')} />;
    }

    // error or not auth'd:
    return <Navigate to="/login" />;
  },
});
